import { useEffect } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePolicyOptions } from '@/shared/api/reference'
import { policyOptionLabel } from '@/shared/lib/planName'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  IDENTIFICATION_METHODS,
  IDENTIFICATION_STRENGTHS,
  PORTAL_SUPPORTED_METHODS,
  VENUE_FUNDINGS,
  createZone,
  hasPortalScreen,
  methodBlockedByStrength,
  updateZone,
  type IdentificationMethod,
  type IdentificationStrength,
  type VenueFunding,
  type VenueZone,
} from './venueApi'

interface ZoneFormModalProps {
  open: boolean
  venueId: string
  /** `null` = yangi zona */
  zone: VenueZone | null
  onClose: () => void
}

interface FormValues {
  code: string
  name: string
  ssid?: string
  nasIdentifier?: string
  nasIp?: string
  policyId?: string
  sessionQuotaGb?: number | null
  sessionSeconds?: number | null
  grantValidMinutes?: number | null
  allowedMethods: IdentificationMethod[]
  minStrength?: IdentificationStrength
  deviceGraceMinutes?: number | null
  funding?: VenueFunding
}

const BYTES_IN_GB = 1024 ** 3

/**
 * Zona — bitta SSID / bitta NAS nuqtasi ob'ekt ichida.
 *
 * ⚠️ `allowedMethods` BO'SH BO'LMASLIGI KERAK — backend buni majburlaydi
 * (`VenueZoneDraft.java`: bo'sh ro'yxat zonani identifikatsiyasiz ochib
 * yuborardi). Shuning uchun frontendda ham `required` va kamida bitta
 * tanlov majburiy.
 */
export function ZoneFormModal({ open, venueId, zone, onClose }: ZoneFormModalProps) {
  const { t, i18n } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const policies = usePolicyOptions(open)

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      zone
        ? {
            code: zone.code,
            name: zone.name,
            ssid: zone.ssid ?? undefined,
            nasIdentifier: zone.nasIdentifier ?? undefined,
            nasIp: zone.nasIp ?? undefined,
            policyId: zone.policyId ?? undefined,
            sessionQuotaGb: zone.sessionQuotaBytes === null ? null : zone.sessionQuotaBytes / BYTES_IN_GB,
            sessionSeconds: zone.sessionSeconds,
            grantValidMinutes: zone.grantValidMinutes,
            allowedMethods: [...zone.allowedMethods],
            minStrength: zone.minStrength,
            deviceGraceMinutes: zone.deviceGraceMinutes,
            funding: zone.funding ?? undefined,
          }
        : {
            allowedMethods: [],
            minStrength: 'DELEGATED',
          },
    )
  }, [open, zone, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const draft = {
        code: values.code.trim(),
        name: values.name.trim(),
        ssid: values.ssid?.trim() || null,
        nasIdentifier: values.nasIdentifier?.trim() || null,
        nasIp: values.nasIp?.trim() || null,
        policyId: values.policyId ?? null,
        sessionQuotaBytes:
          values.sessionQuotaGb === null || values.sessionQuotaGb === undefined
            ? null
            : Math.round(values.sessionQuotaGb * BYTES_IN_GB),
        sessionSeconds: values.sessionSeconds ?? null,
        grantValidMinutes: values.grantValidMinutes ?? null,
        allowedMethods: values.allowedMethods,
        minStrength: values.minStrength ?? null,
        deviceGraceMinutes: values.deviceGraceMinutes ?? null,
        funding: values.funding ?? null,
      }
      return zone ? updateZone(zone.id, draft) : createZone(venueId, draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venues.zones(venueId) })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={zone ? t('zone.editTitle') : t('zone.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={zone ? t('common.save') : t('operation.create')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={600}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="code"
          label={t('zone.code')}
          rules={[
            { required: true, message: t('zone.codeRequired') },
            { pattern: /^[a-z0-9][a-z0-9-]{0,30}$/, message: t('zone.codeFormat') },
          ]}
        >
          <Input autoFocus autoComplete="off" />
        </Form.Item>

        <Form.Item name="name" label={t('zone.name')} rules={[{ required: true, message: t('zone.nameRequired') }]}>
          <Input />
        </Form.Item>

        <Form.Item name="ssid" label={t('zone.ssid')}>
          <Input />
        </Form.Item>

        <Form.Item name="nasIdentifier" label={t('zone.nasIdentifier')} extra={t('zone.nasIdentifierHint')}>
          <Input />
        </Form.Item>

        <Form.Item name="nasIp" label={t('zone.nasIp')}>
          <Input placeholder="10.10.20.2" />
        </Form.Item>

        <Form.Item name="policyId" label={t('zone.policy')} extra={t('zone.policyHint')}>
          <Select
            allowClear
            loading={policies.length === 0}
            options={policies.map((p) => ({
              value: p.id,
              label: policyOptionLabel(p, i18n.language),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="allowedMethods"
          label={t('zone.allowedMethods')}
          extra={t('zone.allowedMethodsHint')}
          rules={[{ required: true, message: t('zone.allowedMethodsRequired') }]}
        >
          <Select
            mode="multiple"
            /*
              ⚠️ Portal ekran chizmagan usul roʻyxatda QOLADI, oʻchirilmaydi:
              backend uni qabul qiladi va xodim qoʻlida (`STAFF_MANUAL`)
              yoki kelajakdagi ekran uchun oldindan sozlab qoʻyish mumkin.
              Lekin nomi yonida belgi turadi — operator nimani tanlayotganini
              KO'RSIN, keyin mehmondan eshitmasin.
            */
            options={IDENTIFICATION_METHODS.map((m) => ({
              value: m,
              label: PORTAL_SUPPORTED_METHODS.includes(m)
                ? t(`identificationMethod.${m}`)
                : `${t(`identificationMethod.${m}`)} — ${t('zone.methodNoPortalScreenShort')}`,
            }))}
          />
        </Form.Item>

        {/*
          Tanlangan usullarning HECH BIRI portalda chizilmagan bo'lsa —
          bu zona mehmonni ichkariga umuman kirita olmaydi. Saqlashni
          TO'XTATMAYDI (xodim qo'lida ochish stsenariysi qonuniy), lekin
          jimgina o'tkazib yuborilmaydi.
        */}
        <Form.Item
          noStyle
          shouldUpdate={(prev: FormValues, next: FormValues) =>
            prev.allowedMethods !== next.allowedMethods
          }
        >
          {({ getFieldValue }) => {
            const chosen = (getFieldValue('allowedMethods') ?? []) as IdentificationMethod[]
            if (chosen.length === 0 || hasPortalScreen(chosen)) return null
            return (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
                message={t('zone.noPortalScreenAtAll')}
                description={t('zone.noPortalScreenHint')}
              />
            )
          }}
        </Form.Item>

        <Form.Item name="minStrength" label={t('zone.minStrength')} extra={t('zone.minStrengthHint')}>
          <Select
            options={IDENTIFICATION_STRENGTHS.map((s) => ({ value: s, label: t(`identificationStrength.${s}`) }))}
          />
        </Form.Item>

        {/*
          ⚠️ USUL VA MINIMAL KUCH BIR-BIRIGA ZID BO'LSA.

          Sinovda topilgan holat: zonada `VOUCHER` ruxsat etilgan,
          `minStrength` esa `SUFFICIENT`. Panel buni jimgina saqlagan.
          Mehmon portalda vaucher tugmasini KO'RGAN, HAQIQIY kodni
          kiritgan va "bu yerda shaxsni tasdiqlash uchun yetarli emas"
          javobini olgan.

          Bu ekran chizilmagan holatdan YOMONROQ: u yerda usul umuman
          ko'rinmaydi, bu yerda esa ishlaydigandek tuyuladi. Shuning
          uchun ogohlantirish AYNAN `minStrength` maydonining tagida —
          operator qiymatni o'zgartirayotgan paytda ko'rsin.
        */}
        <Form.Item
          noStyle
          shouldUpdate={(prev: FormValues, next: FormValues) =>
            prev.allowedMethods !== next.allowedMethods || prev.minStrength !== next.minStrength
          }
        >
          {({ getFieldValue }) => {
            const chosen = (getFieldValue('allowedMethods') ?? []) as IdentificationMethod[]
            const min = (getFieldValue('minStrength') ?? null) as IdentificationStrength | null
            const blocked = chosen.filter((m) => methodBlockedByStrength(m, min))
            if (blocked.length === 0) return null
            return (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
                message={t('zone.methodTooWeak')}
                description={t('zone.methodTooWeakHint', {
                  methods: blocked.map((m) => t(`identificationMethod.${m}`)).join(', '),
                  strength: min ? t(`identificationStrength.${min}`) : '',
                })}
              />
            )
          }}
        </Form.Item>

        <Form.Item
          name="sessionQuotaGb"
          label={t('zone.sessionQuotaGb')}
          extra={t('zone.sessionQuotaHint')}
        >
          <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder={t('billing.unlimited')} />
        </Form.Item>

        <Form.Item name="sessionSeconds" label={t('zone.sessionSeconds')} extra={t('zone.sessionSecondsHint')}>
          <InputNumber min={0} style={{ width: '100%' }} placeholder={t('billing.unlimited')} />
        </Form.Item>

        <Form.Item
          name="grantValidMinutes"
          label={t('zone.grantValidMinutes')}
          extra={t('zone.grantValidMinutesHint')}
        >
          <InputNumber min={5} max={43200} style={{ width: '100%' }} placeholder="720" />
        </Form.Item>

        <Form.Item
          name="deviceGraceMinutes"
          label={t('zone.deviceGraceMinutes')}
          extra={t('zone.deviceGraceMinutesHint')}
        >
          <InputNumber min={0} max={43200} style={{ width: '100%' }} placeholder="0" />
        </Form.Item>

        <Form.Item name="funding" label={t('zone.funding')} extra={t('zone.fundingHint')}>
          <Select
            allowClear
            placeholder={t('zone.fundingInherit')}
            options={VENUE_FUNDINGS.map((f) => ({ value: f, label: t(`venueFunding.${f}`) }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
